import React, { useEffect } from 'react';
import { Save } from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Logo from '../../../components/common/Logo';
import api from '../../../services/api';
import { toast } from 'react-toastify';

function Toggle({ checked, onChange }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className={`relative w-12 h-6 rounded-full transition-colors duration-200 focus:outline-none ${
        checked ? 'bg-[#1F3A60]' : 'bg-gray-300'
      }`}
    >
      <span
        className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
          checked ? 'translate-x-6' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

function TagInput({ value = [], onChange }) {
  console.log({value});
  const [input, setInput] = React.useState('');

  const addTag = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && input.trim()) {
      e.preventDefault();
      const newTag = input.trim();
      if (!value.includes(newTag)) {
        onChange([...value, newTag]);
      }
      setInput('');
    }
  };

  const removeTag = (tag) => {
    onChange(value.filter(t => t !== tag));
  };

  return (
    <div className="flex flex-wrap items-center gap-2 px-3 py-2 border border-gray-200 rounded-xl bg-gray-50 min-h-[52px]">
      {value.map((tag, index) => (
        <span
          key={`${tag}-${index}`}
          className="flex items-center gap-1 px-3 py-1 bg-white border border-gray-200 rounded-md text-[17px] text-gray-700"
        >
          {tag}
          <button
            onClick={() => removeTag(tag)}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          >
            ×
          </button>
        </span>
      ))}

      <input
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={addTag}
        placeholder={value.length === 0 ? 'Type and press Enter...' : ''}
        className="flex-1 min-w-[80px] bg-transparent outline-none text-[17px] text-gray-700 placeholder-gray-400"
      />
    </div>
  );
}

export default function Settings() {
  const queryClient = useQueryClient();

  const { register, handleSubmit, control, reset, formState: { errors } } = useForm({
    defaultValues: {
      display_name: '',
      support_email: '',
      new_password: '',
      pilgrim_plan_price: '',
      content_moderation_is_enabled: true,
      content_moderation_filtering_words: [],
      custom_announcement: '',
    },
  });

  // Fetch Settings
  const { data, isLoading } = useQuery({
    queryKey: ['settings-data'],
    queryFn: () => api.getSettings(),
    select: (res) => res?.data || {},
  });

  // Populate form using reset() - Cleaner approach
  useEffect(() => {
    if (data) {
      console.log({ content_moderation_filtering_words:Array.isArray(data.content_moderation_filtering_words)})
      reset({
        display_name: data.display_name || '',
        support_email: data.support_email || '',
        pilgrim_plan_price: data.pilgrim_plan_price?.toString() || '',
        content_moderation_is_enabled: data.content_moderation_is_enabled === 1,
        content_moderation_filtering_words: Array.isArray(data.content_moderation_filtering_words) 
          ? data.content_moderation_filtering_words
          : [],
        custom_announcement: data.custom_announcement || '',
      });
    }
  }, [data, reset]);

  // Update Mutation
  const updateMutation = useMutation({
    mutationFn: (payload) => api.updateSettings(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings-data'] });
      toast.success('Settings updated successfully!');
    },
    onError: (err) => {
      toast.error(err?.message || 'Failed to update settings.');
    },
  });

  const onSubmit = (formData) => {
    const payload = {
      display_name: formData.display_name,
      support_email: formData.support_email,
      content_moderation_is_enabled: formData.content_moderation_is_enabled ? 1 : 0,
      content_moderation_filtering_words: formData.content_moderation_filtering_words,
      custom_announcement: formData.custom_announcement,
    };

    if (formData.pilgrim_plan_price) {
      payload.pilgrim_plan_price = parseFloat(formData.pilgrim_plan_price);
    }
    if (formData.new_password?.trim()) {
      payload.new_password = formData.new_password;
    }

    updateMutation.mutate(payload);
  };

  return (
    <div style={{ fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }} className="bg-gray-50 p-0 sm:p-0 xl:p-0">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5 mb-8">
        <div>
          <h1 className="text-[22px] sm:text-[28px] md:text-[33px] lg:text-[30px] 2xl:text-[30px] font-bold text-gray-900 mb-1 leading-tight">
            Settings
          </h1>
          <p className="text-gray-500 text-[14px] sm:text-[15px] md:text-[16px] lg:text-[17px]">
            Configure your platform settings
          </p>
        </div>
      </div>

      {/* Main Card */}
      <div className="bg-white rounded-3xl p-4 sm:p-6 lg:p-8">
        {isLoading ? (
          <div className="text-center py-12">Loading settings...</div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            {/* General Settings */}
            <div>
              <h2 className="text-[20px] font-bold text-gray-900 mb-6">General Settings</h2>

              <div className="w-20 h-20 rounded-full overflow-hidden flex-shrink-0 bg-black flex items-center justify-center mb-6">
                <Logo className="w-14 h-14 object-contain" />
              </div>

              <div className="flex flex-wrap gap-[22px]">
                <div className="w-full max-w-[624px]">
                  <label className="block text-[17px] font-medium text-gray-700 mb-2">Display Name</label>
                  <input
                    {...register("display_name")}
                    className="w-full h-[52px] px-4 border border-gray-200 rounded-xl text-[17px] text-gray-700 bg-gray-50 outline-none focus:border-[#1F3A60]"
                  />
                </div>

                <div className="w-full max-w-[624px]">
                  <label className="block text-[17px] font-medium text-gray-700 mb-2">Support Email</label>
                  <input
                    {...register("support_email")}
                    className="w-full h-[52px] px-4 border border-gray-200 rounded-xl text-[17px] text-gray-700 bg-gray-50 outline-none focus:border-[#1F3A60]"
                  />
                </div>
              </div>
            </div>

            {/* Password */}
            <div className="w-full max-w-[624px]">
              <label className="block text-[17px] font-medium text-gray-700 mb-2">New Password</label>
              <input
                type="password"
                {...register("new_password")}
                placeholder="Leave blank to keep current password"
                className="w-full h-[52px] px-4 border border-gray-200 rounded-xl text-[17px] text-gray-700 bg-gray-50 outline-none focus:border-[#1F3A60]"
              />
            </div>

            <hr className="my-8 border-gray-300" />

            {/* Subscription Settings */}
            <div>
              <h2 className="text-[20px] font-bold text-gray-900 mb-6">Subscription Settings</h2>
              <div className="w-full max-w-[624px]">
                <label className="block text-[17px] font-medium text-gray-700 mb-2">Pilgrim Monthly Price</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-[17px]">$</span>
                  <input
                    type="number"
                    step="0.01"
                    {...register("pilgrim_plan_price")}
                    className="w-full h-[52px] pl-8 pr-4 border border-gray-200 rounded-xl text-[17px] text-gray-700 bg-gray-50 outline-none focus:border-[#1F3A60]"
                  />
                </div>
              </div>
            </div>

            <hr className="my-8 border-gray-300" />

            {/* Platform Configuration */}
            <div>
              <h2 className="text-[22px] font-bold text-gray-900 mb-6">Platform Configuration</h2>

              {/* Auto Moderation */}
              <div className="flex items-start justify-between gap-4 mb-8">
                <div>
                  <p className="text-[17px] font-semibold text-gray-800 mb-1">Content Auto-Moderation</p>
                  <p className="text-sm text-gray-500">Automatically flag suspicious content</p>
                </div>
                <Controller
                  name="content_moderation_is_enabled"
                  control={control}
                  render={({ field }) => <Toggle checked={field.value} onChange={field.onChange} />}
                />
              </div>

              {/* Filtering Words */}
              <div className="mb-8 w-full max-w-[624px]">
                <label className="block text-[17px] font-medium text-gray-700 mb-2">Filtering Words</label>
                <Controller
                  name="content_moderation_filtering_words"
                  control={control}
                  render={({ field }) => (
                    <TagInput value={field.value} onChange={field.onChange} />
                  )}
                />
              </div>

              {/* Announcement */}
              <div className="mb-10 w-full max-w-[624px]">
                <label className="block text-[17px] font-medium text-gray-700 mb-2">Custom Announcement</label>
                <textarea
                  {...register("custom_announcement")}
                  rows={5}
                  placeholder="Display a message banner for all users..."
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-[17px] text-gray-700 bg-gray-50 outline-none focus:border-[#1F3A60] resize-none placeholder-gray-400"
                />
              </div>
            </div>

            {/* Save Button */}
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="h-[52px] px-6 rounded-xl bg-[#1F3A60] hover:bg-[#172e4e] transition-colors text-white text-[17px] font-medium flex items-center justify-center gap-2 shadow-sm disabled:opacity-60"
            >
              <Save size={16} />
              {updateMutation.isPending ? 'Saving...' : 'Update Settings'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}